import React, { Fragment, useEffect, useState } from "react";
import { Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { ERRORS } from "../constants/user-info.const";
import Loader from "../common/Loader";
import EmployeeForm from "./forms/EmployeeForm";
import { useAppState } from "../../provider/AppProvider";
import { updateEmployee } from "../../services/api-services/adpAPIService";
import { showAlert, showInfo } from "../../store/actions/feedback";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { UiText } from "../common/ui/UiText";

const validationSchema = () =>
    Yup.object({
        first_name: Yup.string().required(ERRORS.firstName),
        middle_initial: Yup.string(),
        last_name: Yup.string().required(ERRORS.lastName),
        email: Yup.string()
            .email(ERRORS.validEmail)
            .max(255)
            .required(ERRORS.email),
        date_of_birth: Yup.string().required(ERRORS.dob),
        ssn: Yup.string(),
        street_1: Yup.string().required(ERRORS.street),
        street_2: Yup.string(),
        city: Yup.string().required(ERRORS.city),
        state: Yup.object().required(ERRORS.state).nullable(),
        zip: Yup.string().required(ERRORS.zipCode),
        bank_name: Yup.string().required("Bank name is required"),
        routing_number: Yup.string().required("Routing number is required"),
        account_number: Yup.string().required("Bank number is required"),
    });

const EmployeeDetails = () => {
    const { config, isApiLoading, employeeDetails } = useAppState();
    const states = config?.employees?.states ?? [];
    const [loading, setLoading] = useState<boolean>(false);
    const preSelectedState =
        states?.find(
            (v) => v.id === employeeDetails?.associateLegalAddress.stateCode
        ) ?? {};
    const [initialValues, setInitialValues] = useState<any>({
        state: preSelectedState,
        city: '',
        street_2: '',
        street_1: '',
        zip: '',
    });
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const showErrorMessage = (error) => {
        const errorMessage = error?.status?.message ?? "Error";
        dispatch(
            showAlert({
                alertType: "error",
                alertText: errorMessage,
            })
        );
    };

    useEffect(() => {
        if (employeeDetails) {
            const data: any = {
                first_name: employeeDetails.legalName.firstName,
                middle_initial: employeeDetails.legalName.middleName,
                last_name: employeeDetails.legalName.lastName,
                email: employeeDetails.personalEmailAddress,
                ssn: "",
                date_of_birth: employeeDetails.birthDate ?? '',
                street_1: employeeDetails.associateLegalAddress.address1,
                street_2: employeeDetails.associateLegalAddress.address2 ? employeeDetails.associateLegalAddress.address2: '',
                city: employeeDetails.associateLegalAddress.city,
                state: preSelectedState,
                zip: employeeDetails.associateLegalAddress.postalCode,
                countryCode: "",
            };
            if (employeeDetails.moneyDistributionInstructions) {
                data.account_number =
                    employeeDetails.moneyDistributionInstructions.accountNumber;
                data.routing_number =
                    employeeDetails.moneyDistributionInstructions.routingNumber;
                data.bank_name =
                    employeeDetails.moneyDistributionInstructions.moneyDistributionInstructionName;
            }
            setInitialValues(data);
        }
    }, [employeeDetails, states]);

    const onSubmit = (data: any, formHelpers: FormikHelpers<any>) => {
        setLoading(true);
        const enteredData: any = {
            legalName: {
                firstName: data.first_name,
                middleName: data.middle_initial,
                lastName: data.last_name,
            },
            email: data.email,
            birthDate: data.date_of_birth,
            personalAddress: {
                address1: data.street_1,
                address2: data.street_2 ? data.street_2 : "",
                city: data.city,
                stateCode: data.state.id,
                postalCode: data.zip,
            },
        };
        if (data.ssn) {
            enteredData.documentNumber = data.ssn.replace(
                /^(\d{3})(\d{2})(\d{4})$/,
                "$1-$2-$3"
            );
        }
        enteredData.paymentMethod = {
            accountNumber: data.account_number,
            routingNumber: data.routing_number,
            moneyDistributionInstructionName: data.bank_name,
        };
        updateEmployee(enteredData)
            .then((response) => {
                setLoading(false);
                navigate("/adp-info-submitted");
            })
            .catch((error) => {
                setLoading(false);
                if(error.errors?.length > 0 && error.errors[0]?.address1) {
                    dispatch(
                        showInfo({
                            infoData: (
                                <Fragment>
                                    <UiText> Possible Correct address could be </UiText>
                                    <ul>
                                        {error.errors.map((address: any) => (
                                            <li key={address}>{address?.address1}, {address?.address2}, {address?.city}, {address?.postalCode}, {address?.stateCode}</li>
                                        ))}
                                    </ul>
                                </Fragment>
                            ),
                        })
                    )
                }
                if (error?.errors?.length) {
                    error?.errors?.forEach((err: any) => {
                        dispatch(
                            showAlert({
                                alertType: "error",
                                alertText: err?.message || err?.vocabulary,
                            })
                        );
                    });
                } else {
                    showErrorMessage(error);
                }
            });
    };
    if (isApiLoading) {
        return <Loader />;
    }
    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema()}
            onSubmit={onSubmit}
            enableReinitialize
        >
            {({ values, setFieldValue }) => {
                return (
                    <Form>
                        <EmployeeForm
                            states={states ?? []}
                            formValues={values}
                            loading={loading}
                            setFieldValue={setFieldValue}
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};

export default EmployeeDetails;
